import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { Home, Map, Bell, Plus, User as UserIcon } from "lucide-react";
import Avatar from "@/components/Avatar";

/**
 * Mobile-first fixed bottom navigation.
 * Five slots: Feed · Map · (floating Create) · Inbox · Me
 * Hidden on md+ screens — desktop uses the top Navbar.
 */
export default function BottomNav() {
  const { pathname } = useLocation();
  const { user, profile } = useAuth();
  const { unreadNotifs = 0 } = useRealtime() || {};
  const navigate = useNavigate();

  const isActive = (p, exact = false) => exact ? pathname === p : pathname.startsWith(p);

  const goCreate = () => navigate(user ? "/create" : "/login?next=/create");

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-paper border-t-[3px] border-ink"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="relative h-20 px-3 flex items-end justify-between gap-1">
        <Tab to="/feed"          icon={Home} label="Feed"  active={isActive("/feed") || pathname === "/"} />
        <Tab to="/map"           icon={Map}  label="Map"   active={isActive("/map")} />

        {/* Floating create button */}
        <button
          onClick={goCreate}
          data-testid="bottom-nav-create"
          aria-label="Create event"
          className="absolute left-1/2 -translate-x-1/2 -top-5 w-16 h-16 bg-lime border-[3px] border-ink rounded-full shadow-pop active:translate-x-[2px] active:translate-y-[2px] active:shadow-pop-active inline-flex items-center justify-center transition-[transform,box-shadow]"
        >
          <Plus size={28} strokeWidth={3} className="text-ink" />
        </button>
        <div className="w-16 shrink-0" aria-hidden />

        <Tab
          to="/notifications"
          icon={Bell}
          label="Inbox"
          active={isActive("/notifications") || isActive("/messages")}
          badge={unreadNotifs}
        />
        <MeTab
          to={user ? "/profile" : "/login"}
          active={isActive("/profile") || isActive("/u/")}
          avatarUrl={profile?.avatar_url}
          name={profile?.full_name || profile?.display_name || user?.email || "Me"}
          signedIn={!!user}
        />
      </div>
    </nav>
  );
}

function Tab({ to, icon: Icon, label, active, badge = 0 }) {
  return (
    <Link
      to={to}
      data-testid={`bottom-nav-${label.toLowerCase()}`}
      className="relative flex-1 flex flex-col items-center justify-end gap-1 py-2 select-none"
    >
      <span className={`relative inline-flex w-11 h-11 items-center justify-center rounded-full transition-colors ${
        active ? "bg-ink text-paper" : "text-ink"
      }`}>
        <Icon size={22} strokeWidth={2.2} />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-lime text-ink text-[10px] font-extrabold leading-[18px] text-center rounded-full border-2 border-ink">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      <span className={`text-[11px] font-bold uppercase tracking-widest ${active ? "text-ink" : "text-ink/55"}`}>
        {label}
      </span>
    </Link>
  );
}

function MeTab({ to, active, avatarUrl, name, signedIn }) {
  return (
    <Link
      to={to}
      data-testid="bottom-nav-me"
      className="relative flex-1 flex flex-col items-center justify-end gap-1 py-2 select-none"
    >
      <span className={`relative inline-flex w-11 h-11 items-center justify-center rounded-full transition-colors ${
        active ? "ring-[3px] ring-ink" : ""
      }`}>
        {signedIn ? (
          <Avatar src={avatarUrl} name={name} size="sm" />
        ) : (
          <span className="w-10 h-10 rounded-full border-[2.5px] border-ink flex items-center justify-center bg-paper">
            <UserIcon size={20} strokeWidth={2.2} className="text-ink" />
          </span>
        )}
      </span>
      <span className={`text-[11px] font-bold uppercase tracking-widest ${active ? "text-ink" : "text-ink/55"}`}>
        Me
      </span>
    </Link>
  );
}
